import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { MessageActions } from "./MessageActions";
import {
  useHoveredMessageApi,
  useHoveredMessageState,
} from "./use-hovered-message";

/**
 * Singleton MessageActions — one instance for the whole message list,
 * positioned absolutely over the currently-hovered row. Replaces
 * mounting `<MessageActions />` inside every `<Message />` (which
 * scaled badly: 200 rows × ~7 Radix `<Tooltip>` instances each = 1400
 * simultaneous Radix mounts on a shift-up).
 *
 * Behavior:
 *  • Reads `hovered` payload from `useHoveredMessage` context.
 *  • Position is driven by CSS custom properties `--hovered-pill-top`
 *    and `--hovered-pill-right`, written synchronously by the provider
 *    inside `setHovered`. The pill repositions in the same paint as
 *    the hover transition — no React re-render in the critical path.
 *  • Portal'd to `document.body`, fixed-positioned. Same offsets as
 *    the old inline pill (`-top-3` / `right-2`).
 *  • Provider imperatively toggles `actions-open` on the row element
 *    in the same JS task, so the row highlight + pill position move
 *    together.
 *  • Hides on scroll (Slack pattern) — `clearImmediate` tears down
 *    pill + locked state in one shot; user re-hovers, pill reappears.
 *  • Cancels pending clear on its own mouseenter so the cursor can
 *    move row→pill across the small gap without flicker.
 *  • Locked state (emoji picker open) suppresses clear-on-mouseleave
 *    via `clearHovered`'s lock check.
 */
export function HoveredMessageActions() {
  // Singleton subscribes to BOTH contexts: stable API for callbacks,
  // reactive state for `hovered` / `locked`. Per-row Messages use
  // `useHoveredMessageApi` only, so they don't re-render on hover.
  const api = useHoveredMessageApi();
  const { hovered } = useHoveredMessageState();

  // Note: `actions-open` class management lives in the provider
  // (`use-hovered-message.tsx`) and runs SYNCHRONOUSLY inside
  // `setHovered` / `clearHovered` / `clearImmediate`, in the same JS
  // task as the mouse event. Doing it via an effect here (even
  // `useLayoutEffect`) created a race with the browser's native
  // `:hover` flip — one paint would show the OLD row still classed
  // and the NEW row already :hovered, producing a visible
  // double-highlight on fast cursor movement.

  // Hide on scroll. Listen in capture so containers that don't bubble
  // (overflow scrollers) still notify us. Use the immediate (non-
  // debounced) clear path — the debounce on `clearHovered` exists for
  // the row→pill mouse-handoff and would otherwise cause the pill to
  // appear to "follow" the row for ~80ms while the user is scrolling.
  // The immediate path also unlocks: if the user has the emoji picker
  // open and scrolls, MessageActions' own scroll listener will close
  // the picker, but we want pill + lock to tear down together rather
  // than race.
  useEffect(() => {
    if (!hovered) return;
    const onScroll = () => {
      api?.clearImmediate();
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [hovered, api]);

  // Callback bundle. Reads handlers fresh from `getHandlers()` on each
  // invocation so the row's latest closure fires (row props can change
  // between mouseenter and click — `isPinned`, for instance). After
  // each action, dismiss the pill: matches the legacy per-row behavior
  // of `setShowActions(false)` after every callback.
  //
  // `availability` snapshot decides which buttons render. Captured at
  // memo time (hover-change boundary) — recomputed on every new
  // hover. Threadability / pin-availability rarely flips for a given
  // message in a single hover lifespan.
  const handlers = useMemo(() => {
    if (!hovered || !api) return null;
    const get = hovered.getHandlers;
    const initial = get();
    const after = () => {
      api.setLocked(false);
      api.clearHovered();
    };
    return {
      onEdit: () => {
        get().onEdit?.();
        after();
      },
      onDelete: () => {
        get().onDelete?.();
        after();
      },
      onReact: (emoji: string) => {
        get().onReact?.(emoji);
        after();
      },
      onInlineReply: initial.onInlineReply
        ? () => {
            get().onInlineReply?.();
            after();
          }
        : undefined,
      onCreateThread: initial.onCreateThread
        ? () => {
            get().onCreateThread?.();
            after();
          }
        : undefined,
      onPin: initial.onPin
        ? () => {
            get().onPin?.();
            after();
          }
        : undefined,
      onSendToShell: initial.onSendToShell
        ? () => {
            get().onSendToShell?.();
            after();
          }
        : undefined,
    };
  }, [hovered, api]);

  if (!api || !hovered || !handlers) return null;

  // Extract render-time values into locals so the JSX accesses plain
  // variables instead of nested property lookups on `hovered` (which
  // the lint rule is content to leave alone after destructuring).
  const isOwn = hovered.isOwn;
  const isPinned = hovered.isPinned;

  return createPortal(
    <div
      style={{
        position: "fixed",
        // CSS vars are set synchronously inside `setHovered` (provider)
        // each time the cursor enters a new row. Reading from vars
        // here means React doesn't need to re-render to reposition —
        // the pill snaps to the new row in the same paint as the
        // hover transition.
        top: "var(--hovered-pill-top, 0px)",
        right: "var(--hovered-pill-right, 0px)",
        zIndex: 50,
      }}
      onMouseEnter={api.cancelClear}
      onMouseLeave={api.clearHovered}
    >
      <MessageActions
        open={true}
        onLockOpen={api.setLocked}
        onEdit={handlers.onEdit}
        onDelete={handlers.onDelete}
        onReact={handlers.onReact}
        onInlineReply={handlers.onInlineReply}
        onCreateThread={handlers.onCreateThread}
        onPin={handlers.onPin}
        onSendToShell={handlers.onSendToShell}
        isOwn={isOwn}
        isPinned={isPinned}
      />
    </div>,
    document.body,
  );
}
